import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Avatar } from './Avatar';
import { roleLabel } from '@/components/wom/theme';
import { LANGUAGES, TIME_ZONES } from './options';
import { ArrowLeft, Upload, Trash2, Lock, KeyRound, Save } from 'lucide-react';

const nativeSelect = 'w-full rounded-md border border-gray-200 px-3 py-2 text-sm text-slate-700 bg-white';

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-semibold text-slate-600">{label}</Label>
    {children}
  </div>
);

const SelfProfile = () => {
  const { token, user: me, login } = useContext(AuthContext);
  const navigate = useNavigate();
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
  const fileRef = useRef(null);
  const [profile, setProfile] = useState(null);
  const [bump, setBump] = useState(0);
  const [pwOpen, setPwOpen] = useState(false);

  const load = () => axios.get(`${API}/me`, authHeaders).then((r) => setProfile(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const set = (k, v) => setProfile((p) => ({ ...p, [k]: v }));

  const save = async () => {
    try {
      await axios.put(`${API}/me`, {
        full_name: profile.full_name || profile.name,
        mobile_number: profile.mobile_number,
        office_number: profile.office_number,
        preferred_language: profile.preferred_language,
        time_zone: profile.time_zone,
      }, authHeaders);
      toast.success('Profile saved');
      // refresh AuthContext user name if changed
      const meR = await axios.get(`${API}/auth/me`, authHeaders);
      login(token, meR.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save');
    }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`${API}/me/photo`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Photo updated');
      setBump((b) => b + 1);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Photo upload failed');
    }
  };

  const removePhoto = async () => {
    try {
      await axios.delete(`${API}/me/photo`, authHeaders);
      toast.success('Photo removed');
      setBump((b) => b + 1);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not remove photo');
    }
  };

  if (!profile) {
    return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;
  }

  const locked = profile.photo_locked;

  return (
    <div className="min-h-screen bg-[#F5F6F8]">
      <header className="sticky top-0 z-30 flex items-center gap-3 bg-white border-b border-gray-200 px-4 lg:px-8 py-3">
        <button onClick={() => navigate('/')} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0E2A47]" data-testid="profile-back-btn">
          <ArrowLeft size={16} /> Back
        </button>
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">My Profile</span>
      </header>

      <main className="px-4 lg:px-8 py-6 max-w-3xl mx-auto" data-testid="self-profile">
        {profile.must_change_password && (
          <div className="mb-5 flex items-center gap-3 rounded-lg border border-[#E8C99B] bg-[#FBF3E4] px-4 py-3 text-sm text-[#8a4a15]" data-testid="temp-password-banner">
            <KeyRound size={18} className="shrink-0" />
            <span>You're using a temporary password. Please set a new one for your account.</span>
            <button onClick={() => setPwOpen(true)} className="ml-auto rounded-md bg-[#C8641E] px-3 py-1.5 text-xs font-bold text-white whitespace-nowrap" data-testid="banner-change-pw-btn">Change now</button>
          </div>
        )}

        <Card className="mb-5">
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <Avatar userId={profile.id} hasPhoto={!!profile.photo_url} name={profile.full_name || profile.name} size={72} bump={bump} />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-extrabold text-[#0E2A47]">{profile.full_name || profile.name}</h2>
                  {locked && <Badge className="bg-[#0E2A47] text-white text-[10px]"><Lock size={10} className="mr-1" />Photo locked</Badge>}
                </div>
                <div className="text-sm text-slate-500">{profile.email}</div>
                <div className="text-xs text-[#8FC93A] font-semibold uppercase tracking-wide mt-0.5">{roleLabel(profile.role)}</div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e.target.files?.[0])} data-testid="profile-photo-input" />
                  <button disabled={locked} onClick={() => fileRef.current?.click()} data-testid="profile-photo-upload"
                    className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-gray-50 disabled:opacity-40">
                    <Upload size={13} /> {profile.photo_url ? 'Replace Photo' : 'Upload Photo'}
                  </button>
                  {profile.photo_url && (
                    <button disabled={locked} onClick={removePhoto} data-testid="profile-photo-remove"
                      className="inline-flex items-center gap-1.5 rounded-md border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:opacity-40">
                      <Trash2 size={13} /> Remove
                    </button>
                  )}
                </div>
                {locked && <p className="mt-1.5 text-[11px] text-slate-400">Your photo is locked by an administrator and cannot be changed.</p>}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5 space-y-4">
            <h3 className="text-sm font-bold text-[#0E2A47]">Contact & Preferences</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Full Name">
                <Input value={profile.full_name || profile.name || ''} onChange={(e) => set('full_name', e.target.value)} data-testid="profile-fullname" />
              </Field>
              <Field label="Mobile Number">
                <Input value={profile.mobile_number || ''} onChange={(e) => set('mobile_number', e.target.value)} data-testid="profile-mobile" />
              </Field>
              <Field label="Office Number">
                <Input value={profile.office_number || ''} onChange={(e) => set('office_number', e.target.value)} data-testid="profile-office" />
              </Field>
              <Field label="Time Zone">
                <select className={nativeSelect} value={profile.time_zone || 'America/New_York'} onChange={(e) => set('time_zone', e.target.value)} data-testid="profile-timezone">
                  {TIME_ZONES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </Field>
            </div>
            <div className="flex flex-wrap gap-2 pt-2">
              <button onClick={save} className="inline-flex items-center gap-1.5 rounded-md bg-[#0E2A47] px-5 py-2 text-sm font-semibold text-white hover:bg-[#16324F]" data-testid="profile-save-btn">
                <Save size={15} /> Save Changes
              </button>
              <button onClick={() => setPwOpen(true)} className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-5 py-2 text-sm font-semibold text-slate-700 hover:bg-gray-50" data-testid="profile-changepw-btn">
                <KeyRound size={15} /> Change Password
              </button>
            </div>
          </CardContent>
        </Card>
      </main>

      {pwOpen && <ChangePasswordDialog onClose={() => { setPwOpen(false); load(); }} />}
    </div>
  );
};

const ChangePasswordDialog = ({ onClose }) => {
  const { token } = useContext(AuthContext);
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
  const [cur, setCur] = useState('');
  const [nw, setNw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (nw.length < 6) return toast.error('New password must be at least 6 characters');
    if (nw !== confirm) return toast.error('Passwords do not match');
    setBusy(true);
    try {
      await axios.post(`${API}/me/change-password`, { current_password: cur, new_password: nw }, authHeaders);
      toast.success('Password changed');
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not change password');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="change-password-dialog">
        <DialogHeader><DialogTitle className="text-[#0E2A47]">Change Password</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <Input type="password" placeholder="Current password" value={cur} onChange={(e) => setCur(e.target.value)} data-testid="cp-current" />
          <Input type="password" placeholder="New password" value={nw} onChange={(e) => setNw(e.target.value)} data-testid="cp-new" />
          <Input type="password" placeholder="Confirm new password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="cp-confirm" />
          <DialogFooter className="gap-2">
            <button onClick={onClose} className="rounded-md border border-gray-200 px-4 py-2 text-sm font-semibold text-slate-600">Cancel</button>
            <button onClick={submit} disabled={busy} className="rounded-md bg-[#0E2A47] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" data-testid="cp-submit">{busy ? 'Working…' : 'Change Password'}</button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SelfProfile;
